import type { Schemas } from '../../../api/client';

export type Purpose = NonNullable<Schemas['Payment']>['purpose'];
export type MethodKind = Schemas['PaymentMethod']['kind'];
export type Ledger = 'RECEIVABLE' | 'PAYABLE';

export const PURPOSES: Record<
  Purpose,
  { label: string; short: string; ledger: Ledger; party: 'customer' | 'supplier'; inflow: boolean }
> = {
  RECEIPT: {
    label: 'Received from customer',
    short: 'Received',
    ledger: 'RECEIVABLE',
    party: 'customer',
    inflow: true,
  },
  SUPPLIER_PAYMENT: {
    label: 'Paid to supplier',
    short: 'Paid',
    ledger: 'PAYABLE',
    party: 'supplier',
    inflow: false,
  },
  CUSTOMER_REFUND: {
    label: 'Refund to customer',
    short: 'Refund',
    ledger: 'RECEIVABLE',
    party: 'customer',
    inflow: false,
  },
  SUPPLIER_REFUND: {
    label: 'Refund from supplier',
    short: 'Refund',
    ledger: 'PAYABLE',
    party: 'supplier',
    inflow: true,
  },
};

export const METHOD_KINDS: Record<MethodKind, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  CARD: 'Card',
  CHEQUE: 'Cheque',
  OTHER: 'Other',
};

/** Human names for open-item, allocation and credit document types. */
export const DOC_TYPES: Record<string, string> = {
  SALES_INVOICE: 'Invoice',
  SUPPLIER_INVOICE: 'Bill',
  OPENING_BALANCE: 'Opening balance',
  REFUND: 'Refund',
  PAYMENT: 'Payment',
  CREDIT_NOTE: 'Credit note',
  DEBIT_NOTE: 'Debit note',
};

export function docType(type: string): string {
  return DOC_TYPES[type] ?? type.replace(/_/g, ' ').toLowerCase();
}

export const ALLOCATION_METHODS: Record<string, string> = {
  AUTO_FIFO: 'Oldest first',
  EXPLICIT: 'Chosen',
  MANUAL: 'Reallocated',
};

/**
 * Where documents of other milestones live. Kept in one place so the paths can be aligned once
 * those screens land.
 */
export const links = {
  payment: (id: string) => `/payments/${id}`,
  contact: (id: string, party: 'customer' | 'supplier') =>
    party === 'customer' ? `/customers/${id}` : `/suppliers/${id}`,
  salesInvoice: (id: string) => `/sales/invoices/${id}`,
  supplierInvoice: (id: string) => `/purchases/bills/${id}`,
  goodsReceipt: (id: string) => `/purchases/receipts/${id}`,
  salesReturn: (id: string) => `/returns/sales/${id}`,
  creditNote: (id: string) => `/returns/credit-notes/${id}`,
  purchaseReturn: (id: string) => `/returns/purchase/${id}`,
  debitNote: (id: string) => `/returns/debit-notes/${id}`,
};

/** A link for an allocation target or credit source, when there is a screen for it. */
export function docLink(type: string, id: string): string | null {
  switch (type) {
    case 'SALES_INVOICE':
      return links.salesInvoice(id);
    case 'SUPPLIER_INVOICE':
      return links.supplierInvoice(id);
    case 'PAYMENT':
    case 'REFUND':
      return links.payment(id);
    case 'CREDIT_NOTE':
      return links.creditNote(id);
    case 'DEBIT_NOTE':
      return links.debitNote(id);
    default:
      return null;
  }
}

/** Today in the shop's time zone as YYYY-MM-DD. */
export function todayIn(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}
